import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  headers: { 'Content-Type': 'application/json' },
});

/* Attach JWT token to every request */
api.interceptors.request.use(config => {
  const token = localStorage.getItem('ef_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

/* On 401 — clear token and redirect to login.
   IMPORTANT: do NOT redirect on auth endpoints (login, verify-otp,
   forgot-password, reset-password) — those legitimately return 401
   on bad input, and the page should stay so the user sees the error. */
const PUBLIC_AUTH_PATHS = [
  '/auth/login',
  '/auth/signup',
  '/auth/verify-otp',
  '/auth/resend-verification',
  '/auth/forgot-password',
  '/auth/reset-password',
];

api.interceptors.response.use(
  res => res,
  err => {
    const url = err.config?.url || '';
    const isPublicAuthCall = PUBLIC_AUTH_PATHS.some(p => url.includes(p));
    if (err.response?.status === 401 && !isPublicAuthCall) {
      localStorage.removeItem('ef_token');
      localStorage.removeItem('ef_user');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

export default api;

/* ── Auth ───────────────────────────────────────────── */
export const authAPI = {
  signup:             (data)  => api.post('/auth/signup', data),
  login:              (data)  => api.post('/auth/login',  data),
  verifyOtp:          (data)  => api.post('/auth/verify-otp', data),
  resendVerification: (data)  => api.post('/auth/resend-verification', data),
  forgotPassword:     (data)  => api.post('/auth/forgot-password', data),
  resetPassword:      (data)  => api.post('/auth/reset-password', data),
  me:                 ()      => api.get('/auth/me'),
  updateProfile:      (data)  => api.patch('/auth/me', data),
  changePassword:     (data)  => api.patch('/auth/me/password', data),
};

/* ── User ───────────────────────────────────────────── */
export const userAPI = {
  dashboard:   ()     => api.get('/user/dashboard'),
  earnings:    (page) => api.get(`/user/earnings?page=${page||1}`),
  activity:    (page) => api.get(`/user/activity?page=${page||1}`),
  withdrawals: ()     => api.get('/user/withdrawals'),
};

/* ── Withdrawals ────────────────────────────────────── */
export const withdrawalAPI = {
  submit: (data) => api.post('/withdrawals', data),
  list:   ()     => api.get('/withdrawals'),
};

/* ── Admin ───────────────────────────────────────────── */
export const adminAPI = {
  stats:             ()          => api.get('/admin/stats'),
  users:             (p, s)     => api.get(`/admin/users?page=${p}&search=${s||''}`),
  createUser:        (data)     => api.post('/admin/users', data),
  updateUser:        (id, data) => api.patch(`/admin/users/${id}`, data),
  earnings:          (page)     => api.get(`/admin/earnings?page=${page||1}`),
  addEarning:        (data)     => api.post('/admin/earnings', data),
  updateEarning:     (id, data) => api.patch(`/admin/earnings/${id}`, data),
  deleteEarning:     (id)       => api.delete(`/admin/earnings/${id}`),
  clearPending:      (userId, target_status) => api.post(`/admin/users/${userId}/clear-pending`, { target_status }),
  withdrawals:       (status)   => api.get(`/admin/withdrawals${status ? `?status=${status}` : ''}`),
  resolveWithdrawal: (id, data) => api.patch(`/admin/withdrawals/${id}`, data),
  activity:          (page)     => api.get(`/admin/activity?page=${page||1}`),
};

/* ── Platform Accounts ──────────────────────────────── */
export const platformAPI = {
  list:   ()     => api.get('/platform-accounts'),
  submit: (data) => api.post('/platform-accounts', data),
};

/* ── Platforms list (used in PlatformAccounts page) ─── */
export const PLATFORMS = [
  'Appen', 'Scale AI', 'Remotasks', 'Toloka', 'Prolific',
  'Outlier AI', 'DataAnnotation', 'Clickworker', 'iSoftStone',
  'Lionbridge', 'Hive Microtasks', 'TELUS AI',
];
